import Helper from '../helpers/Helper';
import { FilterRequest, FilterValue, OrderValue, OrderTypes, FilterOperations } from './FilterModels';

export default class FilterHelper {
    static getParamWithFilter(filter?: FilterRequest | null): URLSearchParams {
        const params = new URLSearchParams();
        if (!filter) {
            params.append('full', '');
        } else {
            if (filter.isFull) {
                params.append('full', '');
            } else {
                params.append('page', filter.page.toString());
                params.append('limit', filter.limit.toString());
            }

            if (filter.tree) params.append('tree', '');
            if (filter.summary) params.append('summary', filter.summary);
            if (filter.keyword) params.append('q', filter.keyword);

            filter.filters?.forEach((x: FilterValue) => {
                const hasAnd = x.hasAnd ? ':and' : '';
                params.append(`f.${x.propertyName}:${FilterHelper.getAcronymOperator(x.operator)}${hasAnd}`, x.values.join('|'));
            });

            filter.orders = filter.orders?.sort((a, b) => a.index - b.index);
            const orderValue = filter.orders?.map(x => `${x.type === OrderTypes.Desc ? '-' : ''}${x.propertyName}`).join(',');
            if (orderValue) params.append('sort', orderValue);
        }
        return params;
    }

    static clearParamFilterUrl(params: Record<string, any>): Record<string, any> {
        const filteredParams = { ...params };
        ['sort', 'limit', 'page', 'q', 'full'].forEach(key => delete filteredParams[key]);
        Object.keys(filteredParams).forEach(key => {
            if (key.startsWith('f.')) delete filteredParams[key];
        });
        return filteredParams;
    }

    static getObjectParamWithFilter(filter?: FilterRequest): Record<string, any> {
        const result: Record<string, any> = {};
        if (!filter) {
            result['full'] = '';
        } else {
            if (filter.isFull) result['full'] = '';
            else {
                result['page'] = filter.page;
                result['limit'] = filter.limit;
            }
            if (filter.tree) result['tree'] = '';
            if (filter.summary) result['summary'] = filter.summary;
            if (filter.keyword) result['q'] = filter.keyword;

            filter.filters?.forEach((x: FilterValue) => {
                const hasAnd = x.hasAnd ? ':and' : '';
                result[`f.${x.propertyName}:${FilterHelper.getAcronymOperator(x.operator)}${hasAnd}`] = x.values.join('|');
            });

            filter.orders = filter.orders?.sort((a, b) => a.index - b.index);
            const orderValue = filter.orders?.map(x => `${x.type === OrderTypes.Desc ? '-' : ''}${x.propertyName}`).join(',');
            if (orderValue) result['sort'] = orderValue;
        }
        return result;
    }

    static convertObjectUrlToFilter(objectUrl?: Record<string, any>): FilterRequest {
        const result = new FilterRequest();
        if (!objectUrl) return result;

        Object.entries(objectUrl).forEach(([key, value]) => {
            if (key === 'full') result.isFull = Helper.toBool(value) ?? false;
            else if (key === 'page') result.page = Helper.toNumber(value) ?? 0;
            else if (key === 'limit') result.limit = Helper.toNumber(value) ?? 0;
            else if (key === 'q') result.keyword = value;
            else if (key === 'tree') result.isFull = Helper.toBool(value) ?? false;
            else if (key === 'summary') result.summary = value;
            else if (key === 'sort') {
                result.orders = value.toString().split(',').map((x: string) => {
                    const order = new OrderValue();
                    order.type = x.startsWith('-') ? OrderTypes.Desc : OrderTypes.Asc;
                    order.propertyName = x.replace(/^[-+]/, '').trim();
                    return order;
                });
            } else if (key.startsWith('f.')) {
                const [property, filterOp] = key.substring(2).split(':');
                const filter = new FilterValue();
                filter.propertyName = property;
                filter.operator = FilterHelper.convertAcronymToOperator(filterOp);
                filter.values = value.split('|').filter(Boolean);
                filter.hasAnd = filterOp.endsWith(':and');
                result.filters.push(filter);
            }
        });
        return result;
    }

    static getAcronymOperator(type?: FilterOperations): string {
        const map: Record<FilterOperations, string> = {
            [FilterOperations.NotEquals]: 'neq',
            [FilterOperations.GreaterThan]: 'gt',
            [FilterOperations.LessThan]: 'lt',
            [FilterOperations.GreaterThanOrEqual]: 'gte',
            [FilterOperations.LessThanOrEqual]: 'lte',
            [FilterOperations.Contains]: 'in',
            [FilterOperations.NotContains]: 'not_in',
            [FilterOperations.StartsWith]: 'sw',
            [FilterOperations.EndsWith]: 'ew',
            [FilterOperations.Equals]: 'eq',
        };
        return map[type ?? FilterOperations.Equals];
    }

    static convertAcronymToOperator(str: string): FilterOperations {
        const map: Record<string, FilterOperations> = {
            'neq': FilterOperations.NotEquals,
            'gt': FilterOperations.GreaterThan,
            'lt': FilterOperations.LessThan,
            'gte': FilterOperations.GreaterThanOrEqual,
            'lte': FilterOperations.LessThanOrEqual,
            'in': FilterOperations.Contains,
            'not_in': FilterOperations.NotContains,
            'sw': FilterOperations.StartsWith,
            'ew': FilterOperations.EndsWith,
            'eq': FilterOperations.Equals,
        };
        return map[str.toLowerCase()] ?? FilterOperations.Equals;
    }
}
